import * as React from 'react'
import { v4 as uuid } from 'uuid'
import { Box, Icon, Button, styled } from '@design/pt-ui'
import { FormValue } from '../interfaces/components/dynamic-form'
import { ProjectId } from '../interfaces/entities/project'
import { IBlueprintService } from '../interfaces/services/blueprints'
import { IProjectService } from '../interfaces/services/projects'
import { FormatsetId, Formatset } from '../interfaces/entities/formatset'
import { useDirtySanchez } from '../components/DirtySanchez'

import { ChangeCallback as LiveFormChangeCallback } from '../lib/dynamic-form/LiveForm'
import SimpleModal from '../components/SimpleModal'
import LiveForm from '../lib/dynamic-form/LiveForm'

import DebouncedTextField from './DebouncedTextField'

import DI from '../app/di'
import { sequentialAll } from '../utils/promise'
import { translateWithUserLocale } from '../utils/i18n'
import { DisabledFields } from '../lib/dynamic-form/Form'
import Loader from './Loader'

const Wrapper = styled('div')({
  padding: '0 15px',
})

const NameFieldWrapper = styled('div')({
  '& input': {
    fontSize: '1.3125rem',
    fontWeight: 500,
    lineHeight: '1.16667em',
    width: 400,
  },
})

export interface Props {
  projectId: ProjectId
  formatSetName: string
  formatsetId?: FormatsetId
  onSave?: (formatset: Formatset[]) => Promise<any>

  restricted: boolean
  activator: React.ReactNode
  platform: string
}

type State = {
  open: boolean
  saving: boolean
  validating: string[]
  canSave: boolean
  error: string | null
  formatsets: Array<{ id: string; name: string; value: FormValue }>
}

function FormatsetForm(props: Props) {
  const getInitialState = () => {
    const id = uuid()

    return {
      open: false,
      saving: false,
      validating: [],
      canSave: false,
      error: null,
      formatsets: [
        props.formatSetName && props.formatsetId
          ? {
              id: props.formatsetId,
              name: props.formatSetName,
              value: {},
            }
          : {
              id,
              name: translateWithUserLocale(
                'formatset_modal.default_formatset_name',
              ),
              value: {},
            },
      ],
    }
  }

  const [state, setState] = React.useState<State>(getInitialState())
  const { isAnyDirty, clean, defile } = useDirtySanchez()

  const getEmptyFormatset = () => {
    const id = uuid()
    return [
      {
        id,
        name: translateWithUserLocale('formatset_modal.default_formatset_name'),
        value: {},
      },
    ]
  }

  const getInitializer = (id: string) => async () => {
    const blueprintService = DI.get('BlueprintService') as IBlueprintService

    const response = await blueprintService.initFormatsetForm({
      type: props.platform,
      projectId: props.projectId,
      id: props.formatsetId,
    })

    if (response.type === 'success') {
      const { validation, value } = response.payload
      setState((state) => ({
        ...state,
        formatsets: state.formatsets.map((fs) => {
          if (fs.id === id) {
            return {
              id,
              name: props.formatsetId ? props.formatSetName : fs.name,
              value,
            }
          } else {
            return fs
          }
        }),
        canSave: validation.validUntil === 'end',
      }))
      return response.payload
    }

    throw response.payload
  }

  const getLabel = () => {
    return props.formatsetId ? 'Edit formatset' : 'Create formatset'
  }

  const getChangeHandler: (
    id: string,
    defile: (id: any) => any,
  ) => LiveFormChangeCallback =
    (id, defile: (id: any) => any) =>
    ({ value, validation }) => {
      setState((state) => ({
        ...state,
        formatsets: state.formatsets.map((fs) => {
          if (fs.id === id) {
            return { id, name: fs.name, value }
          } else {
            return fs
          }
        }),
        canSave: validation.validUntil === 'end',
      }))
      defile('formatset')
    }

  const getFormatSetNameUpdater =
    (id: string, defile: (id: any) => any) => (name: string) => {
      setState((state) => ({
        ...state,
        formatsets: state.formatsets.map((fs) => {
          if (fs.id === id) {
            return { ...fs, name }
          } else {
            return fs
          }
        }),
      }))
      defile('formatset')
    }

  const createFormatsets = async () => {
    const projectService = DI.get('ProjectService') as IProjectService
    const responses = await sequentialAll(
      state.formatsets.map((fs) => {
        return () =>
          projectService.createFormatset({
            projectId: props.projectId,
            type: props.platform,
            name: fs.name,
            data: fs.value,
          })
      }),
    )

    return responses
  }

  const handleSave = async () => {
    if (state.saving || !state.canSave) {
      return
    }

    try {
      const projectService = DI.get('ProjectService') as IProjectService
      setState((state) => ({
        ...state,
        saving: true,
      }))

      const response = await (props.formatsetId
        ? projectService.updateFormatset({
            formatsetId: props.formatsetId,
            projectId: props.projectId,
            type: props.platform,
            name: state.formatsets[0].name,
            data: state.formatsets[0].value,
          })
        : createFormatsets())

      if ('length' in response) {
        const error = response.find((r) => {
          return r.type === 'error'
        })
        if (error && error.type === 'error') {
          setState((state) => ({
            ...state,
            saving: false,
            error: error.payload.message,
            formatsets: getEmptyFormatset(),
          }))
        } else {
          if (props.onSave !== undefined) {
            const formatsets = response.map((r) => r.payload) as Formatset[]
            props.onSave(formatsets)
          }
          setState((state) => ({
            ...state,
            saving: false,
            open: false,
            formatsets: getEmptyFormatset(),
          }))
          clean('formatset')
        }
      } else {
        if (response.type === 'success') {
          clean('formatset')
          if (props.onSave !== undefined) {
            props.onSave([response.payload])
          }
          setState((state) => ({
            ...state,
            saving: false,
            open: false,
            formatsets:
              props.formatSetName && props.formatsetId
                ? state.formatsets
                : getEmptyFormatset(),
          }))
        } else {
          setState((state) => ({
            ...state,
            saving: false,
            error: response.payload.message,
            formatsets:
              props.formatSetName && props.formatsetId
                ? state.formatsets
                : getEmptyFormatset(),
          }))
        }
      }
    } catch (e) {
      setState((state) => ({
        ...state,
        saving: false,
        error:
          translateWithUserLocale('error.something_went_wrong_apologetic') +
          '.',
        formatsets: getEmptyFormatset(),
      }))
    }
  }

  const shouldClose = () => {
    return state.saving === false
  }

  const open = () => {
    setState((state) => ({
      ...state,
      open: true,
    }))
  }

  const close = () => {
    if (!shouldClose()) {
      return
    }

    setState((state) => getInitialState())
    clean('formatset')
  }

  const renderForms = (defile: (id: any) => any) => {
    if (state.open) {
      return state.formatsets.map((fs) => {
        const formProps = {
          label: getLabel(),
          platform: props.platform,
          initializer: getInitializer(fs.id),
          autoAddToEmptyArrays: false,
          onChange: getChangeHandler(fs.id, defile),
          onClose:
            props.formatSetName && props.formatsetId
              ? undefined
              : () => removeFormatset(fs.id),
          onValidationStart: () => {
            setState((state) => ({
              ...state,
              validating: [...state.validating, fs.id],
            }))
          },
          onValidationEnd: () => {
            setState((state) => ({
              ...state,
              validating: state.validating.filter((x) => x !== fs.id),
            }))
          },
          colorizeNesting: false,
          disabledFields: props.restricted
            ? ({
                kind: 'except',
                fields: ['recommended_number'],
              } as DisabledFields)
            : undefined,
          disabledOptionTooltip: translateWithUserLocale(
            'formatset_modal.not_available_tooltip',
          ),
        }
        return (
          <Wrapper key={fs.id}>
            <LiveForm
              {...formProps}
              label={
                <NameFieldWrapper>
                  <DebouncedTextField
                    text={{
                      label: translateWithUserLocale(
                        'formatset_modal.formatset_name',
                      ),
                    }}
                    value={fs.name}
                    onChange={getFormatSetNameUpdater(fs.id, defile)}
                    id="cp-formatset-name-input"
                  />
                </NameFieldWrapper>
              }
            />
          </Wrapper>
        )
      })
    }

    return null
  }

  const addFormatset = () => {
    const id = uuid()
    setState((state) => ({
      ...state,
      formatsets: [
        ...state.formatsets,
        {
          id,
          name: translateWithUserLocale(
            'formatset_modal.default_formatset_name',
          ),
          value: {},
        },
      ],
    }))
  }

  const removeFormatset = (id: string) => {
    setState((state) => ({
      ...state,
      formatsets: state.formatsets.filter((fs) => {
        return fs.id !== id
      }),
    }))
  }

  return (
    <Wrapper>
      <SimpleModal
        width={'60vw'}
        title={translateWithUserLocale('formatset_modal.formatsets')}
        open={state.open}
        onOpen={open}
        onClose={close}
        confirmClose={isAnyDirty}
        activator={props.activator}>
        {renderForms(defile)}

        {props.formatsetId == null ? (
          <Button
            sx={{
              mt: 1,
              textTransform: 'none',
              ml: 3,
            }}
            variant="contained"
            disabled={state.saving}
            onClick={addFormatset}>
            <Icon>add </Icon>
            {translateWithUserLocale('formatset_modal.add_formatset')}
          </Button>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column',
            margin: 4,
            marginLeft: 2,
            width: '97%',
            textAlign: 'center',
          }}>
          <Button
            variant="contained"
            sx={{ margin: 1 }}
            disabled={
              state.validating.length > 0 || state.saving || !state.canSave
            }
            onClick={handleSave}>
            {state.saving
              ? translateWithUserLocale('saving')
              : translateWithUserLocale('formatset_modal.save_formatsets')}
            {state.saving ? (
              <Box component="span" sx={{ marginLeft: 2 }}>
                <Loader type="Oval" color="#591841" size="small" />
              </Box>
            ) : null}
          </Button>
        </Box>
      </SimpleModal>
    </Wrapper>
  )
}

export default FormatsetForm
